/**
 * Month, PM, platform and state for Reports ▸ Projects.
 *
 * The suite filter model is shared with every other report, so month is a custom window, PM reuses `owners`,
 * platform reuses `categories` and project state reuses `priorities`. Saved views store those same fields.
 */
import { Button, Picker } from '@patchbay/ui'
import type { FilterOptions } from '../../../lib/api/generated'
import { CLEARED_FILTERS, type ReportsSearch } from '../api'
import { selectClass } from '../filter-bar'
import { PLATFORMS, PROJECT_STATES } from './keys'

function monthEnd(ym: string): string {
  const [year, month] = ym.split('-').map(Number) as [number, number]
  const last = new Date(year, month, 0).getDate()
  return `${ym}-${String(last).padStart(2, '0')}`
}

export function ProjectFilters({
  search,
  onChange,
  options,
}: {
  search: ReportsSearch
  onChange: (patch: Partial<ReportsSearch>) => void
  options: FilterOptions | undefined
}) {
  const month = search.range === 'custom' && search.from ? search.from.slice(0, 7) : ''
  const owners = options?.owners ?? []
  const pm = search.owners?.[0] ?? ''
  const platform = search.categories?.[0] ?? ''
  const state = search.priorities?.[0] ?? ''
  const pmKnown = pm === '' || owners.some((owner) => owner.id === pm)
  const active = Boolean(month || pm || platform || state)

  return (
    <search
      aria-label="Project report filters"
      className="sticky top-0 z-20 flex flex-wrap items-center gap-x-3 gap-y-2 border-b border-border bg-background/95 px-4 py-2.5 backdrop-blur sm:px-5"
    >
      <label className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
        Month
        <input
          type="month"
          aria-label="Month"
          className={selectClass}
          value={month}
          onChange={(event) => {
            const next = event.currentTarget.value
            if (!next) {
              onChange({ range: undefined, from: undefined, to: undefined, grain: undefined })
              return
            }
            onChange({ range: 'custom', grain: 'month', from: `${next}-01`, to: monthEnd(next) })
          }}
        />
      </label>
      <Picker
        aria-label="PM"
        className={selectClass}
        value={pm}
        onChange={(event) => {
          const next = event.currentTarget.value
          onChange({ owners: next ? [next] : undefined })
        }}
      >
        <option value="">All PMs</option>
        {pm && !pmKnown ? <option value={pm}>{pm}</option> : null}
        {owners.map((owner) => (
          <option key={owner.id} value={owner.id}>
            {owner.name}
          </option>
        ))}
      </Picker>
      <Picker
        aria-label="Platform"
        className={selectClass}
        value={platform}
        onChange={(event) => {
          const next = event.currentTarget.value
          onChange({ categories: next ? [next] : undefined })
        }}
      >
        <option value="">All platforms</option>
        {PLATFORMS.map((item) => (
          <option key={item.value} value={item.value}>
            {item.label}
          </option>
        ))}
      </Picker>
      <Picker
        aria-label="State"
        className={selectClass}
        value={state}
        onChange={(event) => {
          const next = event.currentTarget.value
          onChange({ priorities: next ? [next] : undefined })
        }}
      >
        <option value="">All states</option>
        {PROJECT_STATES.map((item) => (
          <option key={item.value} value={item.value}>
            {item.label}
          </option>
        ))}
      </Picker>
      {active ? (
        <Button variant="link" size="sm" onClick={() => onChange(CLEARED_FILTERS)}>
          Clear filters
        </Button>
      ) : null}
    </search>
  )
}
